import { runTriageAnalysis, analyzeImageWithVision, extractHandwrittenText } from './triageService';
import { generateForensicReport } from './reportGenerationService';
import { generateImageAnnotations, saveAnnotations, generateAnnotatedImageFile, uploadAnnotatedImage } from '../annotationService';
import { updateCaseStatus, saveClarifyingQuestions, getClarifyingQuestions, saveAIRun, saveReport, calculateAIRunCost } from '../caseService';
import { getCaseFileUrl } from '../storageService';
import { supabase } from '../../lib/supabase';
import { generateReportPDF, uploadReportPDF } from '../pdfGenerationService';

// Add this block - missing helper functions
function updateProgress(phase, status, message) {
  console.log(`[${phase}] ${status}: ${message}`);
}

// Use centralised pricing from caseService
function calculateCost(usage, model) {
  return calculateAIRunCost(usage, model);
}
// End of added block

export async function runPhase1Triage(caseId, caseData, uploads) {
  try {
    const imageUploads = (uploads || [])?.filter(u => u?.is_image);

    if (imageUploads?.length === 0) {
      console.warn('No image uploads found — proceeding with text-only triage. uploads received:', uploads?.length);
    }

    // Phase 1: Triage Analysis with Vision
    updateProgress('phase1', 'in_progress', 'Analyzing images and extracting handwritten text...');
    
    const imageDescriptions = [];
    const ocrResults = [];
    let phase1Cost = 0;

    for (const upload of uploads) {
      if (upload?.file_type?.startsWith('image/') || upload?.is_image) {
        try {
          const { data: signedUrlData } = await supabase?.storage
            ?.from('case-uploads')
            ?.createSignedUrl(upload?.storage_path, 3600);

          if (signedUrlData?.signedUrl) {
            const imageResponse = await fetch(signedUrlData?.signedUrl);
            const imageBlob = await imageResponse?.blob();
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader?.result);
              reader.readAsDataURL(imageBlob);
            });

            // Vision analysis for scene description
            try {
              const visionResult = await analyzeImageWithVision(base64, caseData);
              if (visionResult?.success) {
                imageDescriptions?.push(visionResult?.description);
                phase1Cost += calculateCost(visionResult?.usage, 'gpt-4o');
              } else {
                console.warn('Vision analysis failed for', upload?.file_name, visionResult?.error);
              }
            } catch (vErr) {
              console.warn('Vision analysis threw for', upload?.file_name, vErr);
            }

            // OCR extraction for handwritten text
            try {
              const ocrResult = await extractHandwrittenText(base64, upload?.file_name);
              if (ocrResult?.success) {
                await supabase
                  ?.from('case_uploads')
                  ?.update({ extracted_handwritten_text: ocrResult?.data })
                  ?.eq('id', upload?.id);
                phase1Cost += calculateCost(ocrResult?.usage, 'gpt-4o');
                if (ocrResult?.data?.text?.trim()) {
                  ocrResults?.push({
                    uploadId: upload?.id,
                    ocrData: ocrResult?.data
                  });
                }
              } else {
                console.warn('OCR failed for', upload?.file_name, ocrResult?.error);
              }
            } catch (oErr) {
              console.warn('OCR threw for', upload?.file_name, oErr);
            }
          }
        } catch (imgErr) {
          console.warn('Failed to process image', upload?.file_name, imgErr);
        }
      }
    }

    // Include OCR results in triage analysis
    const ocrContext = ocrResults?.length > 0
      ? `\n\nExtracted Handwritten Text:\n${ocrResults?.map(ocr => 
          `File: ${ocr?.ocrData?.file_name}\nText: ${ocr?.ocrData?.text}\nConfidence: ${(ocr?.ocrData?.confidence * 100)?.toFixed(0)}%\nQuality: ${ocr?.ocrData?.quality}`
        )?.join('\n\n')}`
      : '';

    const triageResult = await runTriageAnalysis(
      { ...caseData, ocrContext },
      imageDescriptions
    );

    if (!triageResult?.success) {
      throw new Error(triageResult?.error);
    }

    await saveAIRun(
      caseId,
      'triage',
      'gpt-5.2',
      triageResult?.usage,
      { ...triageResult?.data, ocrContext },
      null
    );

    await updateCaseStatus(caseId, 'intake_submitted', {
      triage_summary: triageResult?.data?.summary,
      triage_confidence: triageResult?.data?.confidence
    });

    if (triageResult?.data?.clarifyingQuestions?.length > 0) {
      await saveClarifyingQuestions(caseId, triageResult?.data?.clarifyingQuestions, 1);

      const hasRequired = triageResult?.data?.clarifyingQuestions?.some(q => q?.priority === 'required');
      if (hasRequired || triageResult?.data?.confidence < 0.7) {
        await updateCaseStatus(caseId, 'needs_clarification');
      }
    }

    const hasRequiredQuestions = triageResult?.data?.clarifyingQuestions?.some(q => q?.priority === 'required');
    const lowConfidence = triageResult?.data?.confidence < 0.7;
    // Only send to clarifying questions when confidence is low AND there are required questions.
    // High confidence with only optional questions => skip straight to report.
    const needsClarification = lowConfidence && hasRequiredQuestions;

    return {
      success: true,
      data: triageResult?.data,
      needsClarification,
    };
  } catch (error) {
    console.error('Phase 1 triage error:', error);
    await saveAIRun(caseId, 'triage', 'gpt-5.2', null, null, error?.message);
    return { success: false, error: error?.message };
  }
}

export async function runFollowUpTriage(caseId, userId, maxRounds = 5) {
  try {
    const { data: caseData } = await supabase
      ?.from('cases')?.select('*')?.eq('id', caseId)?.single();

    // Get all Q&A so far (all rounds)
    const { data: allQA } = await supabase
      ?.from('clarifying_questions')
      ?.select('*')
      ?.eq('case_id', caseId)
      ?.order('round', { ascending: true })
      ?.order('display_order', { ascending: true });

    const currentRound = allQA?.length > 0 ? Math.max(...allQA.map(q => q.round)) : 1;

    if (currentRound >= maxRounds) {
      return { success: true, proceedToReport: true };
    }

    // Analyze ALL uploads (including supplemental ones added during this round)
    const { data: uploads } = await supabase
      ?.from('case_uploads')?.select('*')?.eq('case_id', caseId);

    const imageDescriptions = [];
    for (const upload of uploads?.filter(u => u?.is_image || u?.file_type?.startsWith('image/')) || []) {
      const { data: signed } = await supabase?.storage
        ?.from('case-uploads')
        ?.createSignedUrl(upload?.storage_path, 3600);
      if (!signed?.signedUrl) continue;
      try {
        const blob = await (await fetch(signed.signedUrl)).blob();
        const base64 = await new Promise((res) => {
          const r = new FileReader();
          r.onloadend = () => res(r.result);
          r.readAsDataURL(blob);
        });
        const visionResult = await analyzeImageWithVision(base64, caseData);
        if (visionResult?.success) imageDescriptions.push(visionResult.description);
      } catch (e) {
        console.warn('Supplemental image analysis failed:', e);
      }
    }

    // Build Q&A context from all previous answered rounds
    const qaContext = allQA
      ?.filter(q => q?.answer_text)
      ?.map(q => `Round ${q.round} — Q: ${q.question_text}\nA: ${q.answer_text}`)
      ?.join('\n\n');

    const triageResult = await runTriageAnalysis(
      { ...caseData, previousQA: qaContext },
      imageDescriptions
    );

    if (!triageResult?.success) throw new Error(triageResult?.error);

    const nextRound = currentRound + 1;
    await saveAIRun(caseId, `triage_round_${nextRound}`, 'gpt-4o', triageResult?.usage, triageResult?.data, null);

    const allQuestions = triageResult?.data?.clarifyingQuestions || [];
    const confidence = triageResult?.data?.confidence ?? 0;

    // Dynamic round logic:
    // - If confidence >= 0.7 → AI is satisfied, proceed to report
    // - If confidence < 0.7 AND AI returned questions → ask them
    // - If confidence < 0.7 but no questions → proceed anyway (nothing more to ask)
    const needsMore = confidence < 0.7 && allQuestions.length > 0;

    if (needsMore) {
      await saveClarifyingQuestions(caseId, allQuestions, nextRound);
      await updateCaseStatus(caseId, 'needs_clarification', {
        triage_summary: triageResult?.data?.summary,
        triage_confidence: triageResult?.data?.confidence
      });
      return { success: true, needsMoreClarification: true, round: nextRound };
    }

    await updateCaseStatus(caseId, 'intake_submitted', {
      triage_summary: triageResult?.data?.summary,
      triage_confidence: triageResult?.data?.confidence
    });
    return { success: true, proceedToReport: true };
  } catch (error) {
    console.error('Follow-up triage error:', error);
    return { success: false, error: error?.message };
  }
}

export async function runPhase3ReportGeneration(caseId, userId) {
  try {
    await updateCaseStatus(caseId, 'report_generating');

    const { data: caseData } = await supabase
      ?.from('cases')
      ?.select('*')
      ?.eq('id', caseId)
      ?.single();

    const { data: uploads } = await supabase
      ?.from('case_uploads')
      ?.select('*')
      ?.eq('case_id', caseId);

    const questionsResult = await getClarifyingQuestions(caseId);
    const clarifyingAnswers = questionsResult?.data || [];

    // Load the actual triage findings from the ai_runs table
    // (observableFindings & missingInformation are stored there, not on the cases row)
    // Use ilike to match 'triage', 'triage_round_2', etc.
    const { data: triageRuns } = await supabase
      ?.from('ai_runs')
      ?.select('response_data')
      ?.eq('case_id', caseId)
      ?.ilike('phase', 'triage%')
      ?.order('created_at', { ascending: false })
      ?.limit(1);

    const triageRun = triageRuns?.[0] || null;

    const triageData = {
      summary: caseData?.triage_summary,
      confidence: caseData?.triage_confidence,
      observableFindings: triageRun?.response_data?.observableFindings || [],
      missingInformation: triageRun?.response_data?.missingInformation || []
    };

    // Debug: log what we're feeding to report generation
    console.log('[Phase3] triageData.summary length:', triageData?.summary?.length || 0);
    console.log('[Phase3] observableFindings count:', triageData?.observableFindings?.length);
    console.log('[Phase3] clarifyingAnswers count:', clarifyingAnswers?.length);
    console.log('[Phase3] triageRun phase:', triageRuns?.[0] ? 'found' : 'NOT FOUND');

    const imageAnalysis = [];
    const imageUploads = uploads?.filter(u => u?.is_image);

    // Process images: analyze with Vision, generate annotations, create annotated images
    for (const upload of imageUploads?.slice(0, 30)) {
      const bucket = 'case-uploads';
      const urlResult = await getCaseFileUrl(upload?.storage_path, bucket);
      if (!urlResult?.success) continue;

      const response = await fetch(urlResult?.url);
      const blob = await response?.blob();
      const base64 = await blobToBase64(blob);

      const visionResult = await analyzeImageWithVision(base64, caseData);

      if (visionResult?.success) {
        imageAnalysis?.push({
          uploadId: upload?.id,
          description: visionResult?.description,
          url: urlResult?.url
        });

        // Track Phase 3 vision cost
        await saveAIRun(
          caseId,
          'phase3_vision',
          'gpt-4o',
          visionResult?.usage,
          { uploadId: upload?.id, descriptionLength: visionResult?.description?.length },
          null
        );

        // Generate annotations using OpenAI Vision
        const annotationResult = await generateImageAnnotations(
          base64, 
          visionResult?.description,
          {
            propertyType: caseData?.property_type,
            allegationType: caseData?.allegation_type
          }
        );

        if (annotationResult?.success) {
          // Save image record
          const { data: imageRecord } = await supabase
            ?.from('case_images')
            ?.insert({
              case_id: caseId,
              upload_id: upload?.id,
              image_url: upload?.storage_path,
              caption: visionResult?.description?.substring(0, 200),
              ai_description: visionResult?.description,
              display_order: imageAnalysis?.length - 1
            })
            ?.select()
            ?.single();

          if (imageRecord && annotationResult?.data?.length > 0) {
            // Save annotations to database
            await saveAnnotations(imageRecord?.id, annotationResult?.data);

            // Generate and upload annotated image file
            try {
              const annotatedImageResult = await generateAnnotatedImageFile(
                urlResult?.url,
                annotationResult?.data
              );

              if (annotatedImageResult?.success) {
                await uploadAnnotatedImage(
                  userId,
                  caseId,
                  imageRecord?.id,
                  annotatedImageResult?.blob
                );
              }
            } catch (annotError) {
              console.error('Annotated image generation error:', annotError);
              // Continue even if annotation image fails
            }
          }

          // Track AI usage for annotations
          await saveAIRun(
            caseId,
            'annotation',
            'gpt-4o',
            annotationResult?.usage,
            { annotationCount: annotationResult?.data?.length },
            null
          );
        }
      }
    }

    // Generate forensic report
    const reportResult = await generateForensicReport(
      caseData,
      triageData,
      clarifyingAnswers,
      imageAnalysis,
      caseData?.persona
    );

    if (!reportResult?.success) {
      throw new Error(reportResult?.error);
    }

    // Generate PDF with embedded images and OCR results
    const caseMetadata = {
      caseNumber: caseData?.case_number,
      generatedDate: new Date()?.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      incidentDate: new Date(caseData?.incident_date)?.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      location: caseData?.property_address,
      ocrResults: uploads
        ?.filter(u => u?.extracted_handwritten_text && u?.extracted_handwritten_text?.text?.trim())
        ?.map(u => ({
          file_name: u?.file_name,
          text: u?.extracted_handwritten_text?.text,
          confidence: u?.extracted_handwritten_text?.confidence,
          quality: u?.extracted_handwritten_text?.quality,
          language: u?.extracted_handwritten_text?.language
        }))
    };

    const pdfResult = await generateReportPDF(
      reportResult?.data,
      caseMetadata,
      imageAnalysis?.map(img => ({ url: img?.url, caption: img?.description }))
    );

    let pdfPath = null;
    if (pdfResult?.success) {
      const uploadResult = await uploadReportPDF(pdfResult?.blob, caseId, userId);
      if (uploadResult?.success) {
        pdfPath = uploadResult?.path;
      }
    }

    await saveAIRun(
      caseId,
      'report_generation',
      'gpt-5.2',
      reportResult?.usage,
      reportResult?.data,
      null
    );

    await saveReport(caseId, reportResult?.data, pdfPath);

    await updateCaseStatus(caseId, 'report_ready');

    // Send report ready notification email
    try {
      await fetch(`${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-report-ready-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ caseId, userId })
      });
    } catch (emailError) {
      console.error('Failed to send report ready email:', emailError);
    }

    return {
      success: true,
      data: reportResult?.data
    };
  } catch (error) {
    console.error('Phase 3 report generation error:', error);
    await saveAIRun(caseId, 'report_generation', 'gpt-5.2', null, null, error?.message);
    await updateCaseStatus(caseId, 'intake_submitted');
    return { success: false, error: error?.message };
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader?.result);
    reader.onerror = reject;
    reader?.readAsDataURL(blob);
  });
}